import React, { useEffect, useMemo, useState } from "react";
import "./index.css";
import Dropdown from "react-bootstrap/Dropdown";
import { Autoplay, Pagination } from "swiper/modules";

import {
  Card,
  Container,
  Form,
  Image,
} from "react-bootstrap";
import {
  AreaIcon,
  ArrowLeftIcon,
  CheckMarkIcon,
  DownloadIcon,
  LocationIcon,
  PdfIcon,
  RupeeIcon,
} from "../../../assets/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { goBack } from "../../../utils/otherHelopers";
import { routes } from "../../../routes/pageRoutes";
import { useDispatch, useSelector } from "react-redux";
import { useLoader } from "../../../context/LoaderContext";
import { formatIndianCompact } from "../../../utils/otherHelopers";
// import { routes } from "../../../routes/pageRoutes";
import {
  changePublicStatus,
  changeStatus,
  clearPropertyDetails,
  fetchPropertyDetails,
} from "../../../redux/slice/allPropertiesSlice";
// import { fetchPropertyDetails } from "../../../redux/slice/allPropertiesSlice";
import { Swiper, SwiperSlide } from "swiper/react";
import "./index.css";
import { useToast } from "../../../context/TostContext";
import ConfirmModal from "../../../components/withModal/ConfirmModal";
import { usePermission } from "../../../hooks/usePermission";
import PreviewModal from "../../../components/modals/previewModal";
import { createPortal } from "react-dom";
import StatusChip from "../../../components/statusChip/StatusChip";
import { Armchair, Bath, SquareParking, ChevronLeft, ChevronRight } from "lucide-react";
import { ProfileCard } from "../../../components/custom-card/Cards";

const MODULE_SLUG = "properties";
const renderHeaderContent = (onBack) => (
  <div className="d-flex align-items-center justify-content-between w-100">
    <div className="d-flex align-items-center gap-2">
      <button
        onClick={onBack}
        type="button"
        className="border-0 bg-transparent p-0 d-flex align-items-center justify-content-center primary"
      >
        <ArrowLeftIcon size={22} strokeWidth="2.2" />
      </button>
      <h4 className="fw-semibold mb-0" style={{ color: '#444050', fontSize: '18px' }}>
        Details
      </h4>
    </div>

  </div>
);



const PropertyDetail = () => {
  const { setLoading } = useLoader();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const params = useParams();

  const details = useSelector((state) => state?.allProperties?.propertyDetail);
  const toast = useToast();
  const { hasPermission } = usePermission();
  const canUpdate = hasPermission(MODULE_SLUG, "update");

  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const propertyId = location?.state?.id || params?.id;
  const isPublic = location?.state?.is_public === true || location.pathname.includes("/public-properties") || location.pathname.includes("/public-lands");

  useEffect(() => {
    if (propertyId) {
      dispatch(clearPropertyDetails());
      setLoading(true);
      dispatch(fetchPropertyDetails({ id: propertyId, is_public: isPublic }))
        .then(() => {
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [dispatch, propertyId, setLoading, isPublic]);

  const photos = useMemo(
    () => (Array.isArray(details?.photos) ? details.photos : []),
    [details]
  );

  const hasVideo = !!details?.video?.thumbnail_url || !!details?.video?.url;

  const titleText = useMemo(() => {
    if (!details) return "";

    const bhkText = details?.bhk ? `${details.bhk} BHK` : null;
    const sBuiltupArea = details?.s_builtup_area
      ? `${details.s_builtup_area} ${details.s_builtup_area_unit}`
      : null;
    const propertyType = details?.property_type || null;

    const purpose =
      details?.transaction_type?.toLowerCase() === "sell"
        ? "for Sell" // ✅ keep the capitalization as you asked
        : details?.transaction_type
          ? `for ${details.transaction_type}`
          : "";

    const isLand = details?.property_type?.toLowerCase() === "land";

    // ✅ Priority 1: For Land, show land_area, land_area_unit, land_type, and transaction type
    if (isLand) {
      const landArea =
        details?.land_area && details?.land_area_unit
          ? `${details.land_area} ${details.land_area_unit}`
          : details?.land_area;
      const landType = details?.land_type || null;

      if (landArea && landType && purpose) {
        return `${landArea} ${landType} ${purpose}`;
      }
      if (landArea && purpose) {
        return `${landArea} ${purpose}`;
      }

      return `Land ${purpose}`;
    }

    // ✅ Priority 2: "2 BHK, Flat for Sell"
    if (bhkText && propertyType && purpose) {
      return `${bhkText}, ${propertyType} ${purpose}`;
    }

    // ✅ Priority 3: show property_type + purpose
    // if (propertyType && purpose) {
    //   return `${propertyType} ${purpose}`;
    // }

    if (sBuiltupArea && propertyType && purpose) {
      return `${sBuiltupArea} ${propertyType} ${purpose}`;
    }

    // ✅ Priority 4: society name fallback
    if (details?.society_project_name) {
      return `${details.society_project_name} ${purpose}`;
    }

    // ✅ Fallback
    return `Property ${purpose}`.trim();
  }, [details]);

  const totalPrice = useMemo(() => {
    if (details?.final_price) return details.final_price;
    if (details?.price_total) return details.price_total;
    return null;
  }, [details]);

  const unitPriceText = useMemo(() => {
    if (details?.price_per_unit && details?.price_unit) {
      return `${formatIndianCompact(
        Number(details.price_per_unit).toFixed(2)
      )} / ${details.price_unit}`;
    }
    if (details?.price_per_unit) {
      return formatIndianCompact(details.price_per_unit);
    }
    return null;
  }, [details]);

  const builtupText = useMemo(() => {
    if (details?.s_builtup_area) {
      return `${details.s_builtup_area}${details?.s_builtup_area_unit ? ` ${details.s_builtup_area_unit}` : ""
        }`;
    }
    if (details?.carpet_area) {
      return `${details.carpet_area}${details?.carpet_area_unit ? ` ${details.carpet_area_unit}` : ""
        }`;
    }
    return null;
  }, [details]);



  const downloadPDF = () => {
    const fileUrl = details?.brochure_attachment?.url; // Use the URL of the file

    if (fileUrl) {
      // Open the PDF in a new tab
      window.open(fileUrl, "_blank");
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      // Set loading state before the API call
      setLoading(true);

      const response = await dispatch(
        changeStatus({
          ...(isPublic ? { id: details.property_id } : { id: details.id }),
          status: newStatus,
        })
      ).unwrap();
      if (response?.data?.success) {

        toast.success(`Status updated to ${newStatus}`);
        // Fetch updated property details after status change
        await dispatch(
          fetchPropertyDetails({
            ...(isPublic
              ? { id: details.id, is_public: true }
              : { id: details.id, is_public: false }),
          })
        );
      } else {
        toast.error(response?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error?.message || (typeof error === 'string' ? error : "Failed to update status"));
    } finally {
      setLoading(false); // Reset loading state
    }
  };

  const handleToggle = () => {
    setModalMessage(
      details?.is_public
        ? "This Property will not be visible to other users once unpublished. Do you want to continue?"
        : "This Property will be visible to all users once published. Do you want to continue?"
    );

    setShowModal(true); // Show the modal to confirm
  };

  const handleConfirm = async (status) => {
    setShowModal(false); // Close the modal
    // setLoading(true);

    try {
      // Dispatch action to change the public status
      const response = await dispatch(
        changePublicStatus({
          id: details.id,
          is_public: status,
        })
      ).unwrap(); // Using unwrap to get the response directly

      if (response.data.success) {

        toast.success(`Status successfully updated to ${status ? "Published" : "Unpublished"}`);

        // Fetch updated property details
        await dispatch(fetchPropertyDetails({ id: details.id }));
      } else {
        toast.error(response?.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error(error?.message || (typeof error === 'string' ? error : "Failed to update status"));
    } finally {
      // Show the toast after the operation
      // setShowToast(true);
      // setLoading(false);
    }
  };

  // Back button
  const handleBack = () => {
    goBack(navigate, location, routes.brokerProperties);
  };

  const handleVideoPlay = (url) => {
    setVideoUrl(url);
    setShowVideoModal(true);
  };

  const handleCloseVideoModal = () => {
    setShowVideoModal(false);
    setVideoUrl("");
  };

  const isRent = details?.transaction_type?.toLowerCase() === "rent";
  const isLand = details?.property_type?.toLowerCase() === "land";
  const mediaItems = useMemo(() => {
    const list = photos.map((p) => ({ type: "photo", url: p.url, id: p.id }));
    if (hasVideo && details?.video) {
      list.push({
        type: "video",
        url: details.video.url,
        thumbnail: details.video.thumbnail_url,
        id: details.video.id,
      });
    }
    return list;
  }, [photos, hasVideo, details]);

  const cardTitle = useMemo(() => {
    if (!details) return "";
    const bhkText = details?.bhk ? `${details.bhk} BHK` : null;
    const propertyType = details?.property_type || null;
    if (isLand) {
      const landArea = details?.land_area && details?.land_area_unit
        ? `${details.land_area} ${details.land_area_unit}`
        : details?.land_area;
      const landType = details?.land_type || "";
      return [landArea, landType].filter(Boolean).join(", ") || "Land";
    }
    return [bhkText, propertyType].filter(Boolean).join(", ") || "Property";
  }, [details, isLand]);

  const addressText = useMemo(() => {
    if (!details) return "";
    return isLand
      ? [
        details?.village?.name,
        details?.taluka?.name,
        details?.district?.name,
        details?.state?.name,
      ]
        .filter(Boolean)
        .join(", ")
      : [
        details?.locality?.name,
        details?.city?.name,
        details?.state?.name,
      ]
        .filter(Boolean)
        .join(", ");
  }, [details, isLand]);

  const infoBlocks = useMemo(() => {
    if (!details) return [];
    return [
      unitPriceText && {
        icon: <RupeeIcon />,
        value: `₹ ${unitPriceText}`,
        label: "Unit Price",
      },
      builtupText && {
        icon: <AreaIcon />,
        value: builtupText,
        label: "S.Builtup Area",
      },
      details?.carpet_area && {
        icon: <AreaIcon />,
        value: `${details.carpet_area} ${details?.carpet_area_unit ?? ""}`,
        label: "Carpet Area",
      },
      details?.bhk && {
        icon: <AreaIcon />,
        value: `${details.bhk}`,
        label: "BHK",
      },
    ].filter(Boolean);
  }, [details, unitPriceText, builtupText]);



  const footerItems = useMemo(() => {
    if (!details) return [];
    return [
      details.furnishing_status && {
        icon: <Armchair className="primary" />,
        text: details.furnishing_status,
      },
      details.bath && {
        icon: <Bath className="primary" />,
        text: `${details.bath} Baths`,
      },
      details.parking_car && {
        icon: <SquareParking className="primary" />,
        text: details.parking_car.toLowerCase() === "yes" ? "Car Parking Available" : `${details.parking_car} Car Parking`,
      },
    ].filter(Boolean);
  }, [details]);

  const handleThumbnailClick = (index) => {
    if (swiperInstance) {
      if (swiperInstance.params.loop) {
        swiperInstance.slideToLoop(index);
      } else {
        swiperInstance.slideTo(index);
      }
      setActiveIndex(index);
    }
  };

  const headerPortalTarget = document.getElementById("header-portal-target");
  return (
    <>
      {headerPortalTarget && createPortal(renderHeaderContent(handleBack), headerPortalTarget)}
      <Container fluid className="broker-property-details px-0 grid gap-4">
        <div className="premium-property-card g-col-12 g-col-xl-9 bg-white common-shadow w-100 grid gap-4 p-4 rounded-3">


          {/* Left Side: Image Slider */}
          {(photos.length || hasVideo) && (
            <div className="g-col-12 g-col-md-5">
              <div className="d-flex flex-column gap-3 h-100">
                <div className="main-image-container position-relative rounded overflow-hidden">
                  <Swiper
                    spaceBetween={30}
                    centeredSlides={true}
                    loop={mediaItems.length > 1}
                    pagination={{
                      clickable: true,
                    }}
                    modules={[Autoplay, Pagination]}
                    onSwiper={setSwiperInstance}
                    onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
                    className="w-100 h-100"
                  >
                    {mediaItems.map((item, idx) => (
                      <SwiperSlide
                        key={item.id || idx}
                        className="overflow-hidden d-flex align-items-center justify-content-center h-100 position-relative"
                      >
                        {item.type === "photo" ? (
                          <Image
                            src={item.url}
                            alt="Property"
                            className="w-100 h-100 object-fit-cover"
                          />
                        ) : (
                          <div className="w-100 h-100 position-relative">
                            <Image
                              src={item.thumbnail}
                              alt="Video Thumbnail"
                              className="w-100 h-100 object-fit-cover"
                            />
                            <div
                              onClick={() =>
                                handleVideoPlay(item.url)
                              }
                              className="play-button position-absolute top-50 start-50 translate-middle cursor-pointer"
                            >
                              <svg
                                width="50"
                                height="50"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <polygon
                                  points="5,3 19,12 5,21"
                                  fill="white"
                                />
                              </svg>
                            </div>
                          </div>
                        )}
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  {/* Custom Arrow Navigation Overlay */}
                  {mediaItems.length > 1 && (
                    <>
                      <button
                        type="button"
                        className="swiper-nav-btn swiper-button-prev-custom position-absolute start-0 top-50 translate-middle-y z-3 ms-2"
                        onClick={() => swiperInstance?.slidePrev()}
                      >

                        <ChevronLeft />
                      </button>
                      <button
                        type="button"
                        className="swiper-nav-btn swiper-button-next-custom position-absolute end-0 top-50 translate-middle-y z-3 me-2"
                        onClick={() => swiperInstance?.slideNext()}
                      >
                        <ChevronRight />
                      </button>
                    </>
                  )}
                </div>

                {/* Thumbnail Strip */}
                {mediaItems.length > 1 && (
                  <div className="thumbnail-strip">
                    {mediaItems.slice(0, 5).map((item, idx) => {
                      const is5th = idx === 4;
                      const hasMore = mediaItems.length > 5;
                      const remainingCount = mediaItems.length - 4;
                      return (
                        <div
                          key={item.id || idx}
                          className={`thumbnail-item ${activeIndex === idx ? "active" : ""}`}
                          onClick={() => handleThumbnailClick(idx)}
                        >
                          <Image
                            src={item.type === "photo" ? item.url : item.thumbnail}
                            alt="Thumbnail"
                            className="w-100 h-100 object-fit-cover"
                          />
                          {is5th && hasMore && (
                            <div className="thumbnail-overlay">
                              +{remainingCount}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Right Side: Content */}
          {(titleText ||
            totalPrice ||
            unitPriceText ||
            builtupText) && (
              <div className={`d-flex flex-column ${photos.length || hasVideo ? "g-col-12 g-col-md-7" : "g-col-12"}`}>

                {/* Badges + Status Dropdown */}
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {details?.transaction_type && (
                      <StatusChip pill bg="primary" title={`For ${details.transaction_type}`} />

                    )}
                    {details?.possession && (
                      <StatusChip pill bg="success" title={details.possession} />
                    )}
                    {details?.property_type && (
                      <StatusChip pill bg="info" title={details.property_type} />
                    )}
                  </div>
                  {details?.status && (
                    <Dropdown>
                      <Dropdown.Toggle
                        variant="warning"
                        size="sm"
                        className="rounded-pill d-flex gap-1 align-items-center px-3 py-1 fs-sm fw-medium"
                        id="dropdown-basic"
                        disabled={!canUpdate}
                      >
                        {details.status}
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() => handleStatusChange("Open")}
                          disabled={details.status === "Open"}
                          as="button"
                        >
                          Open
                        </Dropdown.Item>
                        <Dropdown.Item
                          onClick={() => handleStatusChange("Close")}
                          disabled={details.status === "Close"}
                          as="button"
                        >
                          Close
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                </div>

                {/* Title */}
                <h3 className="fw-bold mb-2 text-primary" style={{ fontSize: "24px" }}>
                  {cardTitle}
                </h3>

                {/* Location */}
                {addressText && (() => {
                  const mapUrl = (details?.map_location?.latitude && details?.map_location?.longitude)
                    ? `https://www.google.com/maps?q=${details.map_location.latitude},${details.map_location.longitude}`
                    : `https://www.google.com/maps?q=${encodeURIComponent(addressText)}`;
                  return (
                    <a
                      target="_blank"
                      rel="noopener noreferrer"
                      href={mapUrl}
                      className="d-flex align-items-center gap-2 mb-3 text-decoration-none"
                    >
                      <LocationIcon className="primary" size={16} />
                      <span className="text-secondary fw-medium fs-md">
                        {addressText}
                      </span>
                    </a>
                  );
                })()}















                {/* Info Blocks Grid */}
                {infoBlocks.length > 0 && (
                  <div className="grid gap-3 mb-3">
                    {infoBlocks.map((block, idx) => (
                      <div className="info-box-card g-col-6" key={idx}>
                        <i className="info-box-icon">
                          {block.icon}
                        </i>
                        <div className="info-box-content">
                          <h6 className="info-box-value">{block.value}</h6>
                          <p className="info-box-label">{block.label}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Divider line */}




                {/* Footer Specs Row */}
                {footerItems.length > 0 && (
                  <div className="footer-info-row border-top pt-3 mt-auto">
                    {footerItems.map((item, idx) => (
                      <React.Fragment key={idx}>
                        {idx > 0 && <span className="footer-dot">•</span>}
                        <div className="footer-info-item">
                          {item.icon}
                          <span>{item.text}</span>
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                )}
              </div>
            )}

        </div>

        <div className="g-col-3 d-flex flex-column gap-4" style={{ gridRow: "span 3" }}>
          {details && (
            <Card className="rounded-2 g-col-3 p-4 border-0 common-shadow w-100 common-card flex-row align-items-center justify-content-between">
              <h5 className="fw-medium text-capitalize">Public Status</h5>

              <Form>
                <Form.Check
                  type="switch"
                  id="custom-switch"
                  label={
                    details.is_public ? "Activated" : "Deactivated"
                  }
                  checked={details.is_public}
                  onChange={handleToggle}
                  disabled={!canUpdate}
                />
              </Form>
            </Card>
          )}
          {details?.user && (
            <ProfileCard link={`/user/user-detail/${details?.user?.id}`} title={"Broker Information"} user={{
              name: `${details?.user?.first_name || ""} ${details?.user?.last_name || ""}`.trim(),
              role: details?.user?.profile?.profession?.name || details?.user?.profile?.designation || details?.user?.designation || details?.user?.role_name || details?.user?.role?.name || "Seller",
              image: details?.user?.profile?.profile_photo?.url,
              email: details?.user?.email,
              mobile_no: details?.user?.mobile_number,
            }} />
          )}
          {details?.property_type !== "Land" && (
            <Card className="detail-spec-card p-4 common-shadow g-col-3 w-100">
              <Card.Header className="fw-medium text-capitalize bg-transparent border-0 common-card-header p-0 mb-3">
                Property Details
              </Card.Header>
              <Card.Body className="p-0">


                {details?.bhk && (
                  <div className="detail-spec-item">
                    <p className="detail-spec-label">BHK</p>
                    <p className="detail-spec-value">{details.bhk}</p>
                  </div>
                )}

                {details?.parking_2w && (
                  <div className="detail-spec-item">
                    <p className="detail-spec-label">2-Wheeler Parking Available</p>
                    <p className="detail-spec-value">{details.parking_2w}</p>
                  </div>
                )}

                {details?.floor_no && (
                  <div className="detail-spec-item">
                    <p className="detail-spec-label">Floor No</p>
                    <p className="detail-spec-value">{details.floor_no}</p>
                  </div>
                )}

                {details?.total_floor && (
                  <div className="detail-spec-item">
                    <p className="detail-spec-label">Total Floor</p>
                    <p className="detail-spec-value">{details.total_floor}</p>
                  </div>
                )}

                {details?.flat_floor && (
                  <div className="detail-spec-item">
                    <p className="detail-spec-label">Flat Floor</p>
                    <p className="detail-spec-value">{details.flat_floor}</p>
                  </div>
                )}

                {details?.society_project_name && (
                  <div className="detail-spec-item">
                    <p className="detail-spec-label">Society / Project</p>
                    <p className="detail-spec-value">{details.society_project_name}</p>
                  </div>
                )}

                {details?.developer_name && (
                  <div className="detail-spec-item">
                    <p className="detail-spec-label">Developer</p>
                    <p className="detail-spec-value">{details.developer_name}</p>
                  </div>
                )}




                {details?.city?.name && (
                  <div className="detail-spec-item">
                    <p className="detail-spec-label">City</p>
                    <p className="detail-spec-value">{details.city.name}</p>
                  </div>
                )}

                {details?.locality?.name && (
                  <div className="detail-spec-item">
                    <p className="detail-spec-label">Locality</p>
                    <p className="detail-spec-value">{details.locality.name}</p>
                  </div>
                )}

                {details?.no_of_wings && (
                  <div className="detail-spec-item">
                    <p className="detail-spec-label">No. of wings</p>
                    <p className="detail-spec-value">{details.no_of_wings}</p>
                  </div>
                )}

                {details?.lift && (
                  <div className="detail-spec-item">
                    <p className="detail-spec-label">Lift Available</p>
                    <p className="detail-spec-value">{details.lift}</p>
                  </div>
                )}
              </Card.Body>
            </Card>
          )}



        </div>


        <div className="g-col-9 grid gap-4">
          {details?.brochure_attachment?.url && (
            <Card className="rounded-2 g-col-5 p-3 border-0 common-shadow w-100 common-card card">
              <Card.Header className="px-0 pt-0 bg-transparent border-0 common-card-header">
                <div>
                  <h5 className="fw-medium text-capitalize mb-1">
                    Brochure Attachment
                  </h5>
                </div>
              </Card.Header>
              <Card.Body className="p-4 common-card-body">
                <div className="d-flex align-items-top justify-content-between w-100">
                  <p className="mb-0 d-flex align-items-center gap-2">
                    <PdfIcon
                      width={22}
                      height={22}
                      color="#7367f0"
                      strokeWidth="2"
                    />
                    <span className="sub-head">{details?.brochure_attachment?.key
                      ? details.brochure_attachment.key.split("/").pop()
                      : "No document available"}</span>
                  </p>
                  <p className="cursor-pointer ms-3 text-center" onClick={downloadPDF}>
                    <DownloadIcon
                      width={22}
                      height={22}
                      color="#7367f0"
                      strokeWidth="2"
                    />
                  </p>
                </div>
              </Card.Body>
            </Card>
          )}
          {Array.isArray(details?.amenities) &&
            details.amenities.length > 0 && (
              <Card className="rounded-2 g-col-7 p-4 border-0 common-shadow w-100 common-card">
                <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <h5 className="fw-medium text-capitalize">Amenities</h5>
                  </div>
                </Card.Header>
                <Card.Body className="p-0 common-card-body">
                  <div className="row">
                    {/* Split amenities roughly into two columns without changing CSS */}
                    <div className="col-6">
                      {details.amenities
                        .filter((_, idx) => idx % 2 === 0)
                        .map((am, idx) => (
                          <div
                            className="d-flex gap-1 mb-2"
                            key={`am-l-${idx}`}
                          >
                            <CheckMarkIcon />
                            <p className="p-extra-light mb-0">{am}</p>
                          </div>
                        ))}
                    </div>
                    <div className="col-6">
                      {details.amenities
                        .filter((_, idx) => idx % 2 === 1)
                        .map((am, idx) => (
                          <div
                            className="d-flex gap-1 mb-2"
                            key={`am-r-${idx}`}
                          >
                            <CheckMarkIcon />
                            <p className="p-extra-light mb-0">{am}</p>
                          </div>
                        ))}
                    </div>
                  </div>
                </Card.Body>
              </Card>
            )}

        </div>










        <div className="grid gap-4 g-col-9">
          {details?.notes && (
            <Card className="rounded-3 g-col-6 p-3 border-0 common-shadow common-card">
              <Card.Header className="px-0 pt-0 pb-2 bg-transparent border-0 common-card-header">
                <h6 className="mb-2 npa-details">About Property</h6>
              </Card.Header>
              <Card.Body className="p-0 common-card-body">
                <h6 className="notes">{details.notes}</h6>
              </Card.Body>
            </Card>
          )}

          <Card className="detail-spec-card g-col-6 p-4 bg-white rounded-3 common-shadow">
            <Card.Body className="p-0">
              <div className="detail-spec-item">
                <p className="detail-spec-label">Property Type</p>
                <p className="detail-spec-value">{details?.property_type}</p>
              </div>
              <div className="detail-spec-item">
                <p className="detail-spec-label">Transaction Type</p>
                <p className="detail-spec-value">{details?.transaction_type}</p>
              </div>
              <div className="detail-spec-item">
                <p className="detail-spec-label">Possession</p>
                <p className="detail-spec-value">{details?.possession}</p>
              </div>
              <div className="detail-spec-item">
                <p className="detail-spec-label">City</p>
                <p className="detail-spec-value">{details?.city?.name}</p>
              </div>

            </Card.Body>
          </Card>
        </div>


        <div className="g-col-12 g-col-xl-4">
          <div className="grid column-gap-4 column-gap-xxl-0">
            {isLand &&
              (details?.survey_no ||
                details?.fp_no ||
                details?.tp_no ||
                details?.zone ||
                details?.jantri_rate ||
                details?.dastaveg_value ||
                details?.tenure ||
                details?.road_type ||
                details?.road_size ||
                details?.tags) && (
                <Card className="rounded-2 g-col-12 g-col-lg-6 g-col-xxl-12 p-4 border-0 common-shadow w-100 common-card">
                  <Card.Header className="px-0 pt-0 pb-4 bg-transparent border-0 common-card-header">
                    <div className="d-flex justify-content-between align-items-center gap-3">
                      <h5 className="fw-medium text-capitalize">
                        Land Details
                      </h5>
                    </div>
                  </Card.Header>
                  <Card.Body className="p-0 common-card-body">
                    {details?.survey_no && (
                      <div className="d-flex align-items-center justify-content-between border-bottom">
                        <p className="p-extra-light mb-2">Survey No</p>
                        <p className="p-dark mb-2">{details.survey_no}</p>
                      </div>
                    )}

                    {details?.fp_no && (
                      <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                        <p className="p-extra-light mb-2">FP No</p>
                        <p className="p-dark mb-2">{details.fp_no}</p>
                      </div>
                    )}

                    {details?.tp_no && (
                      <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                        <p className="p-extra-light mb-2">TP No</p>
                        <p className="p-dark mb-2">{details.tp_no}</p>
                      </div>
                    )}

                    {details?.zone && (
                      <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                        <p className="p-extra-light mb-2">Zone</p>
                        <p className="p-dark mb-2">{details.zone}</p>
                      </div>
                    )}

                    {details?.jantri_rate && (
                      <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                        <p className="p-extra-light mb-2">Jantri Rate</p>
                        <p className="p-dark mb-2">
                          {Number(details.jantri_rate).toFixed(2)}
                        </p>
                      </div>
                    )}

                    {details?.dastaveg_value && (
                      <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                        <p className="p-extra-light mb-2">Dastaveg Value</p>
                        <p className="p-dark mb-2">
                          {Number(details.dastaveg_value).toFixed(2)}
                        </p>
                      </div>
                    )}

                    {details?.tenure && (
                      <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                        <p className="p-extra-light mb-2">Tenure</p>
                        <p className="p-dark mb-2">{details.tenure}</p>
                      </div>
                    )}

                    {details?.road_type && (
                      <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                        <p className="p-extra-light mb-2">Road Type</p>
                        <p className="p-dark mb-2">{details.road_type}</p>
                      </div>
                    )}

                    {details?.road_size && (
                      <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                        <p className="p-extra-light mb-2">Road Width</p>
                        <p className="p-dark mb-2">{details.road_size}</p>
                      </div>
                    )}

                    {details?.tags && (
                      <div className="d-flex align-items-center justify-content-between border-bottom mt-2">
                        <p className="p-extra-light mb-2">Tag</p>
                        <p className="p-dark mb-2">{details.tags}</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              )}

            {isRent && (
              <Card className="detail-spec-card g-col-12 g-col-lg-6 g-col-xxl-12 w-100">
                <Card.Body className="p-0">
                  {details?.deposit && (
                    <div className="detail-spec-item">
                      <p className="detail-spec-label">Deposit</p>
                      <p className="detail-spec-value">
                        ₹ {formatIndianCompact(details.deposit)}
                      </p>
                    </div>
                  )}

                  {details?.maintenance_amount && (
                    <div className="detail-spec-item">
                      <p className="detail-spec-label">Maintenance</p>
                      <p className="detail-spec-value">
                        ₹ {formatIndianCompact(details.maintenance_amount)}
                      </p>
                    </div>
                  )}

                  {details?.municipal_tax && (
                    <div className="detail-spec-item">
                      <p className="detail-spec-label">Municipal Tax</p>
                      <p className="detail-spec-value">{details.municipal_tax}</p>
                    </div>
                  )}

                  {details?.contract_duration && (
                    <div className="detail-spec-item">
                      <p className="detail-spec-label">
                        Contract Duration
                      </p>
                      <p className="detail-spec-value">
                        {details.contract_duration}
                      </p>
                    </div>
                  )}

                  {details?.rent_increment && (
                    <div className="detail-spec-item">
                      <p className="detail-spec-label">Rent Increment</p>
                      <p className="detail-spec-value">
                        {details.rent_increment}
                      </p>
                    </div>
                  )}

                  {details?.rent_increment_tenure && (
                    <div className="detail-spec-item">
                      <p className="detail-spec-label">
                        Rent Increment Tenure
                      </p>
                      <p className="detail-spec-value">
                        {details.rent_increment_tenure}
                      </p>
                    </div>
                  )}

                  {details?.notice_period && (
                    <div className="detail-spec-item">
                      <p className="detail-spec-label">Notice Period</p>
                      <p className="detail-spec-value">{details.notice_period}</p>
                    </div>
                  )}

                  {details?.lock_in && (
                    <div className="detail-spec-item">
                      <p className="detail-spec-label">Lock-in</p>
                      <p className="detail-spec-value">{details.lock_in}</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}
          </div>
        </div>


      </Container>
      <ConfirmModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={details?.is_public ? "Unpublish Property" : "Publish Property"}
        message={modalMessage}
        onConfirm={() => handleConfirm(!details?.is_public)}
      />

      <PreviewModal
        show={showVideoModal}
        onHide={handleCloseVideoModal}
        previewMedia={{ type: "video", url: videoUrl }}
      />
    </>
  );
};

export default PropertyDetail;
